import { Injectable } from '@angular/core';

import {
  TEMPLATE_VARIABLE_ITEM_SUFFIX,
  TEMPLATE_VARIABLE_LABELS
} from '../core/constants/template-variable-labels';
import {
  TEMPLATE_VARIABLE_CLASS,
  TEMPLATE_VARIABLE_DATA_ATTRIBUTE
} from '../shared/components/c-rich-text/template-variable.plugin';

/**
 * Traduz variáveis Handlebars para rótulos amigáveis e volta.
 *
 * O valor persistido (FormControl e payload da API) SEMPRE contém o Handlebars
 * cru — a máscara é só camada de apresentação:
 *
 *   API/payload -> maskText/maskHtml -> o que o usuário vê
 *   o que o usuário edita -> unmaskText/unmaskHtml -> API/payload
 *
 * Blocos de controle (`{{#each}}`, `{{#eachTable}}`, `{{/each}}`) NÃO são
 * mascarados: o HandlebarsService depende da posição literal deles em relação
 * às tags `<tr>` para montar as tabelas.
 */
@Injectable({
  providedIn: 'root'
})
export class TemplateVariableService {
  /** `{{Nome}}` ou `{{this.Nome}}` — ignora blocos `{{#...}}` e `{{/...}}`. */
  private readonly VARIABLE_REGEX = /\{\{\s*(?![#/])([A-Za-z0-9_.]+)\s*\}\}/g;

  /**
   * Chip da variável. Casa o formato atual (`data-cred-var`, o widget atômico)
   * e o legado (`data-mention`, do plugin `mention`), porque templates salvos
   * antes desta mudança continuam no banco com o formato antigo.
   */
  private readonly MENTION_SPAN_REGEX =
    /<span[^>]*\sdata-(?:cred-var|mention)="([^"]*)"[^>]*>[\s\S]*?<\/span>/g;

  /**
   * Qualquer expressão Handlebars, blocos incluídos. Usado só no corpo, onde o
   * chip guarda o texto original em `data-mention` e o `unmaskHtml` o restaura
   * literalmente antes do `HandlebarsService` processar as tabelas.
   */
  private readonly EXPRESSION_REGEX = /\{\{[^{}]*\}\}/g;

  /** Marcador temporário usado para proteger chips durante a máscara. */
  private readonly PRESERVED_REGEX = /<!--cred-mention-(\d+)-->/g;

  /**
   * Rótulo amigável de uma expressão (`{{this.Amount}}`, `this.Amount` ou
   * `Amount`). Nomes não mapeados caem no fallback PascalCase -> "Pascal Case".
   */
  label(expression: string): string {
    const inner = expression.replace(/[{}]/g, '').trim();
    const isListScope = inner.startsWith('this.');
    const name = isListScope ? inner.slice('this.'.length) : inner;

    const label = TEMPLATE_VARIABLE_LABELS[name] ?? this.humanize(name);

    return isListScope ? `${label}${TEMPLATE_VARIABLE_ITEM_SUFFIX}` : label;
  }

  /**
   * Assunto: desfaz a máscara e volta a ser uma linha de texto puro, já que o
   * campo agora é um editor (para os widgets de variável) mas o valor
   * persistido continua sendo texto simples.
   */
  unmaskSubject(html: string | null | undefined): string {
    if (!html) return html ?? '';

    return this.stripHtml(this.unmaskHtml(html));
  }

  /**
   * Somente leitura (listagens): troca a sintaxe pelo rótulo, sem colchetes —
   * `Fatura {{BatchNumber}}` -> `Fatura Número do Lote`.
   */
  toPlainLabels(text: string | null | undefined): string {
    if (!text) return text ?? '';

    return text.replace(this.VARIABLE_REGEX, (_match, inner: string) =>
      this.label(inner)
    );
  }

  /** HTML do corpo: `{{Amount}}` -> chip `<span class="mention">Valor</span>`. */
  maskHtml(html: string | null | undefined): string {
    if (!html) return html ?? '';

    // Chips já existentes (templates salvos antes desta máscara) são
    // reconstruídos a partir do `data-mention`, senão continuariam exibindo o
    // Handlebars cru por dentro do chip. Depois saem de cena via placeholder,
    // para o passe de variáveis abaixo não mexer no próprio `data-mention`.
    const preserved: string[] = [];
    const withPlaceholders = html.replace(
      this.MENTION_SPAN_REGEX,
      (match, mention: string) => {
        const expression = this.decode(mention);

        preserved.push(
          expression
            ? this.buildChipFromMention(expression)
            : match
        );

        return `<!--cred-mention-${preserved.length - 1}-->`;
      }
    );

    // Só no texto: uma variável em atributo (`<a href="{{EmailLink}}">`)
    // viraria um `<span>` dentro da tag e quebraria o HTML.
    const masked = withPlaceholders
      .split(/(<[^>]*>)/)
      .map((part, index) =>
        index % 2 === 1
          ? part
          : part.replace(this.EXPRESSION_REGEX, (match: string) =>
              this.buildChipFromMention(match)
            )
      )
      .join('');

    return masked.replace(
      this.PRESERVED_REGEX,
      (_match, index: string) => preserved[Number(index)]
    );
  }

  /** HTML do corpo: chip -> `{{Amount}}`. */
  unmaskHtml(html: string | null | undefined): string {
    if (!html) return html ?? '';

    return html.replace(
      this.MENTION_SPAN_REGEX,
      (match, mention: string) => this.decode(mention) || match
    );
  }

  /**
   * Variáveis que existiam no template original e desapareceram na versão que
   * está sendo salva.
   *
   * Serve de guarda contra a perda silenciosa introduzida pela máscara: o
   * post-fixer do plugin `mention` remove o chip quando o usuário edita o texto
   * dentro dele, e como o Handlebars agora vive só no `data-mention`, o chip
   * removido levaria a variável embora sem deixar rastro na tela.
   */
  missingVariables(
    original: string | null | undefined,
    current: string | null | undefined
  ): { mention: string; label: string; count: number }[] {
    const before = this.countVariables(original);
    const after = this.countVariables(current);

    const missing: { mention: string; label: string; count: number }[] = [];

    before.forEach((count, mention) => {
      const lost = count - (after.get(mention) ?? 0);

      if (lost > 0) {
        missing.push({ mention, label: this.displayLabel(mention), count: lost });
      }
    });

    return missing;
  }

  /**
   * Ocorrências de cada expressão Handlebars. Normaliza com `unmaskHtml` antes
   * de contar: em templates legados a expressão aparece duas vezes (no
   * `data-mention` e no texto do chip) e contaria dobrado.
   */
  private countVariables(html: string | null | undefined): Map<string, number> {
    const counts = new Map<string, number>();

    if (!html) return counts;

    const matches = this.unmaskHtml(html).match(this.EXPRESSION_REGEX) ?? [];

    matches.forEach((mention) => {
      counts.set(mention, (counts.get(mention) ?? 0) + 1);
    });

    return counts;
  }

  /**
   * Monta o chip a partir da expressão Handlebars completa (com as chaves).
   * O valor real fica em `data-mention`; o texto visível é o rótulo.
   */
  private buildChipFromMention(mention: string): string {
    const label = this.displayLabel(mention);

    return `<span class="${TEMPLATE_VARIABLE_CLASS}" ${TEMPLATE_VARIABLE_DATA_ATTRIBUTE}="${this.encode(
      mention
    )}">${this.encode(label)}</span>`;
  }

  /**
   * Texto visível de uma expressão completa, incluindo os helpers de bloco
   * inseridos pelo dropdown (`{{#each Contracts}}`, `{{#eachTable Contracts}}`).
   */
  private displayLabel(mention: string): string {
    const block = mention.match(/^\{\{#(each|eachTable)\s+([A-Za-z0-9_.]+)/);

    if (block) {
      const prefix = block[1] === 'eachTable' ? 'Tabela' : 'Lista';

      return `${prefix}: ${this.label(block[2])}`;
    }

    if (/^\{\{\/each/.test(mention)) return 'Fim da lista';

    // Helper de bloco desconhecido: mantém o texto para não esconder algo que
    // ninguém sabe rotular.
    if (/^\{\{[#/]/.test(mention)) return mention;

    return this.label(mention.replace(/[{}]/g, '').trim());
  }

  /**
   * HTML -> uma linha de texto. Fronteiras de bloco viram espaço para não colar
   * palavras, e entidades voltam a ser caracteres.
   */
  private stripHtml(html: string): string {
    return this.decode(
      html
        .replace(/<\/(p|div|br|h[1-6]|li|tr)>/gi, ' ')
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<[^>]*>/g, '')
    )
      .replace(/\s+/g, ' ')
      .trim();
  }

  private humanize(name: string): string {
    return name
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .trim();
  }

  private encode(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private decode(value: string): string {
    return (
      value
        .replace(/&quot;/g, '"')
        .replace(/&gt;/g, '>')
        .replace(/&lt;/g, '<')
        // O CKEditor emite `&nbsp;` para espaços consecutivos.
        .replace(/&nbsp;/g, ' ')
        .replace(/&#39;/g, "'")
        // `&amp;` por último, senão `&amp;nbsp;` viraria espaço.
        .replace(/&amp;/g, '&')
    );
  }
}
